import cloneDeep from 'lodash/cloneDeep'

import { i18n } from '@/i18n/i18n'
import { ErrorService } from '@/shared/service/errorService'
import {
  type AnyCategory,
  type Category,
  type EditableCategory,
  type Genre,
  type GenreUsage,
  isTemporaryCategory,
  type Tag,
} from '@/shared/types/musicGroup/musicGroup.type'
import { createStore, type GettersObj } from '@/utils/createStore'

import { type Lang } from '../../../../types/TranslatableText.type'
import { GenresService } from '../genres.service'

export type GettersGuestGenres = GettersObj<typeof mapGettersGuestGenres>

export class GuestGenresState {
  genres: Record<string, Genre> = {}
  availableCategories: AnyCategory[] = []
  availableTags: Genre[] = []
  unusedGenreCount = 0
  lastDeletedCount = 0
  lastDeletedCategoriesCount = 0
  deletingGenreIds: string[] = []
  deletingGenreUsages: GenreUsage | null = null
  errorMessage: string | null = null
  selectedGenreRows: Genre[] = []
}

export const {
  guestGenres,
  commit: commitGuestGenres,
  dispatch: dispatchGuestGenres,
  mapGetters: mapGettersGuestGenres,
  mapState: mapStateGuestGenres,
  useGetter: useGetterGuestGenres,
} = createStore({
  namespaced: true,
  moduleName: 'guestGenres',
  initState: new GuestGenresState(),

  actions: {
    async addGenres({ dispatch }, genre: Genre | Tag) {
      try {
        await GenresService.addGenre(genre)
        await dispatch('getGenres')
        await dispatch('loadAvailableCategories', { context: 'ALL' })
        await dispatch('countUnusedGenres')
      } catch (e) {
        ErrorService.handleError(e)
      }
    },

    async getGenres({ commit }) {
      try {
        const genres = await GenresService.getAll()
        commit('SET_GENRES', genres)
      } catch (e) {
        ErrorService.handleError(e)
      }
    },
    async deleteGenre({ commit, dispatch }, genre: Genre) {
      try {
        await GenresService.deleteGenre(genre._id)
        commit('DELETE_GENRE', genre._id)
        await dispatch('countUnusedGenres')
        await dispatch('loadAvailableCategories', { context: 'ALL' })
      } catch (e) {
        ErrorService.handleError(e)
      }
    },

    async updateGenre({ commit, dispatch }, genre: Genre) {
      // Reset error before attempt
      commit('SET_ERROR_MESSAGE', null)
      try {
        const updatedGenre = await GenresService.updateGenre(genre)
        commit('UPDATE_GENRE', updatedGenre)

        // Refresh globally to reflect potential backend re-indexing/shifting
        await dispatch('getGenres')
        await dispatch('loadAvailableCategories', { context: 'ALL' })
      } catch (e) {
        const error = e as { response?: { data?: { message?: string } } }
        const message =
          error.response?.data?.message ?? i18n.t('GENERAL.SNACKBAR_ERROR')
        commit('SET_ERROR_MESSAGE', message)
      }
    },

    async loadAvailableCategories(
      { commit },
      payload: {
        context: 'ALL' | 'GUEST' | 'EVENT'
        guestTypeId?: string
        programId?: string
      },
    ) {
      const { context, guestTypeId, programId } = payload

      try {
        const categories = await GenresService.getAvailableCategories(
          context,
          guestTypeId,
          programId,
        )
        commit('SET_AVAILABLE_CATEGORIES', categories)
      } catch (e) {
        ErrorService.handleError(e)
      }
    },

    async loadAvailableTags({ commit }) {
      try {
        const tags = await GenresService.getAvailableTags()
        commit('SET_AVAILABLE_TAGS', tags)
      } catch (e) {
        ErrorService.handleError(e)
      }
    },

    async countUnusedGenres({ commit }) {
      try {
        const count = await GenresService.countUnused()
        commit('SET_UNUSED_GENRE_COUNT', count)
      } catch (e) {
        ErrorService.handleError(e)
      }
    },

    async deleteUnusedGenres({ commit, dispatch }) {
      try {
        const { deletedTagsCount, deletedCategoriesCount } =
          await GenresService.deleteUnused()
        await dispatch('getGenres')
        await dispatch('countUnusedGenres')
        await dispatch('loadAvailableCategories', { context: 'ALL' })
        commit('SET_LAST_DELETED_COUNT', deletedTagsCount)
        commit('SET_LAST_DELETED_CATEGORIES_COUNT', deletedCategoriesCount)
      } catch (e) {
        ErrorService.handleError(e)
      }
    },

    async prepareDeletion({ commit }, genreId: string) {
      commit('SET_DELETING_GENRE_IDS', [genreId])
      try {
        const usages = await GenresService.getGenreUsages(genreId)
        commit('SET_DELETING_GENRE_USAGES', usages)
      } catch (e) {
        ErrorService.handleError(e)
        commit('SET_DELETING_GENRE_USAGES', null)
      }
    },

    cancelDeletion({ commit }) {
      commit('SET_DELETING_GENRE_IDS', [])
      commit('SET_DELETING_GENRE_USAGES', null)
    },

    async prepareMultipleDeletion({ commit, state }) {
      const genreIds = state.selectedGenreRows.map((g) => g._id)
      commit('SET_DELETING_GENRE_IDS', genreIds)
      try {
        const usages = await GenresService.getUsagesBatch(genreIds)
        commit('SET_DELETING_GENRE_USAGES', usages)
      } catch (e) {
        ErrorService.handleError(e)
        commit('SET_DELETING_GENRE_USAGES', null)
      }
    },

    async deleteMultipleGenres({ commit, dispatch, state }) {
      const genreIds = state.selectedGenreRows.map((g) => g._id)
      try {
        await GenresService.deleteGenresBatch(genreIds)
        await dispatch('getGenres')
        await dispatch('countUnusedGenres')
        await dispatch('loadAvailableCategories', { context: 'ALL' })
        commit('SET_LAST_DELETED_COUNT', genreIds.length)
        commit('SET_SELECTED_GENRE_ROWS', [])
      } catch (e) {
        ErrorService.handleError(e)
      }
    },

    async updateGenresCategory(
      { commit, dispatch },
      {
        ids,
        category,
      }: { ids: string[]; category: Category | EditableCategory },
    ) {
      try {
        await GenresService.updateGenresCategory(ids, category)
        await dispatch('getGenres')
        await dispatch('loadAvailableCategories', { context: 'ALL' })
        commit('SET_SELECTED_GENRE_ROWS', [])
      } catch (e) {
        ErrorService.handleError(e)
        throw e
      }
    },
  },

  getters: {
    getGenres(state) {
      return Object.values(state.genres)
    },

    selectedGenreRows(state) {
      return state.selectedGenreRows
    },

    getGenreById(state) {
      return (id: string): Genre | undefined => {
        return cloneDeep(state.genres[id])
      }
    },

    availableCategories({ availableCategories }) {
      return availableCategories
    },

    availableTags({ availableTags }) {
      return availableTags
    },

    unusedGenreCount({ unusedGenreCount }) {
      return unusedGenreCount
    },

    lastDeletedCount({ lastDeletedCount }) {
      return lastDeletedCount
    },

    lastDeletedCategoriesCount({ lastDeletedCategoriesCount }) {
      return lastDeletedCategoriesCount
    },

    deletingGenreIds({ deletingGenreIds }) {
      return deletingGenreIds
    },

    deletingGenreUsages({ deletingGenreUsages }) {
      return deletingGenreUsages
    },

    errorMessage({ errorMessage }) {
      return errorMessage
    },
  },

  mutations: {
    RESET(state) {
      state.genres = {}
      state.deletingGenreIds = []
      state.deletingGenreUsages = null
      state.selectedGenreRows = []
    },

    SET_GENRES(state, genres: Genre[]) {
      state.genres = genres.reduce(
        (acc: Record<string, Genre>, genre: Genre) => {
          acc[genre._id] = genre
          return acc
        },
        {},
      )
    },

    ADD_AVAILABLE_CATEGORY(state, category: AnyCategory) {
      state.availableCategories.push(category)
    },

    /**
     * Updates an existing category or inserts a new one in the correct order.
     * Also propagates changes to all genres utilizing this category.
     */
    UPDATE_CATEGORY(state, category: Category) {
      if (!category._id) return

      // Helper: Check if names match in both languages (case-insensitive)
      const matchesName = (c: AnyCategory) =>
        (['fr', 'en'] as Lang[]).every(
          (lang) =>
            c.names[lang].trim().toLowerCase() ===
            category.names[lang].trim().toLowerCase(),
        )

      // Find existing category by ID (strict) or Name (fallback for temp categories)
      const index = state.availableCategories.findIndex(
        (c) => c._id === category._id || matchesName(c),
      )

      if (index === -1) {
        // Insert new (maintain sort order by index)
        const insertIndex = state.availableCategories.findIndex(
          (c) => (c.index ?? Infinity) > (category.index ?? 0),
        )
        state.availableCategories.splice(
          insertIndex === -1 ? state.availableCategories.length : insertIndex,
          0,
          category,
        )
      } else {
        // Update existing
        state.availableCategories.splice(index, 1, category)
      }

      // Propagate updates to genres using this category
      let hasUpdates = false
      Object.values(state.genres).forEach((genre) => {
        if (genre.category?._id === category._id) {
          genre.category = category
          hasUpdates = true
        }
      })

      // Trigger reactivity only if actual updates occurred
      if (hasUpdates) {
        state.genres = { ...state.genres }
      }
    },

    REMOVE_TEMPORARY_CATEGORIES(state) {
      state.availableCategories = state.availableCategories.filter(
        (c) => !isTemporaryCategory(c),
      )
    },

    UPDATE_GENRE(state, genre: Genre) {
      state.genres[genre._id] = genre
      state.genres = { ...state.genres }
    },

    DELETE_GENRE(state, genreId: string) {
      delete state.genres[genreId]
      state.genres = { ...state.genres }
      // Also remove from selection if present
      state.selectedGenreRows = state.selectedGenreRows.filter(
        (g) => g._id !== genreId,
      )
    },

    SET_AVAILABLE_CATEGORIES(state, categories: AnyCategory[]) {
      state.availableCategories = cloneDeep(categories)
    },

    SET_AVAILABLE_TAGS(state, tags: Genre[]) {
      state.availableTags = cloneDeep(tags)
    },

    SET_UNUSED_GENRE_COUNT(state, count: number) {
      state.unusedGenreCount = count
    },

    SET_LAST_DELETED_COUNT(state, count: number) {
      state.lastDeletedCount = count
    },

    SET_LAST_DELETED_CATEGORIES_COUNT(state, count: number) {
      state.lastDeletedCategoriesCount = count
    },

    SET_DELETING_GENRE_IDS(state, ids: string[]) {
      state.deletingGenreIds = ids
    },

    SET_DELETING_GENRE_USAGES(state, usages: GenreUsage | null) {
      state.deletingGenreUsages = usages
    },

    SET_ERROR_MESSAGE(state, message: string | null) {
      state.errorMessage = message
    },

    SET_SELECTED_GENRE_ROWS(state, genres: Genre[]) {
      state.selectedGenreRows = genres
    },
  },
})
